import { t as uiText } from '../i18n';
import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { doc, setDoc, updateDoc, deleteDoc, collection, getDocs, query, orderBy, onSnapshot, Unsubscribe, writeBatch } from 'firebase/firestore';
import { auth, db } from '../lib/firebase';
import { useAuth } from './AuthContext';

// フォルダの型定義
export interface FolderData {
  id: string;
  name: string;
  isEditing: boolean;
  parentId?: string;
  ancestorFolderIds?: string[] | null;
  order?: number;
}

interface FolderContextType {
  folders: FolderData[];
  setFolders: React.Dispatch<React.SetStateAction<FolderData[]>>;
  selectedFolderId: string | null;
  setSelectedFolderId: React.Dispatch<React.SetStateAction<string | null>>;
  openFolderMenuId: string | null;
  setOpenFolderMenuId: React.Dispatch<React.SetStateAction<string | null>>;
  openFolderIds: Set<string>;
  setOpenFolderIds: React.Dispatch<React.SetStateAction<Set<string>>>;
  isLoading: boolean;
  error: string | null;
  fetchFolders: (workspaceId: string) => void;
  createFolder: (workspaceId: string, folderData: Omit<FolderData, 'id' | 'isEditing'>) => Promise<string>;
  updateFolder: (workspaceId: string, folderId: string, updates: Partial<FolderData>) => Promise<void>;
  deleteFolder: (workspaceId: string, folderId: string) => Promise<void>;
  clearFolders: () => void;
  toggleFolderOpen: (folderId: string) => void;
}

const FolderContext = createContext<FolderContextType | undefined>(undefined);

export const useFolderContext = () => {
  const context = useContext(FolderContext);
  if (!context) {
    throw new Error('useFolderContext must be used within a FolderProvider');
  }
  return context;
};

interface FolderProviderProps {
  children: ReactNode;
}

export const FolderProvider: React.FC<FolderProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const [folders, setFolders] = useState<FolderData[]>([]);
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
  const [openFolderMenuId, setOpenFolderMenuId] = useState<string | null>(null);
  const [openFolderIds, setOpenFolderIds] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentWorkspaceId, setCurrentWorkspaceId] = useState<string | null>(null);
  const [unsubscribe, setUnsubscribe] = useState<Unsubscribe | null>(null);
  const unsubscribeRef = React.useRef<Unsubscribe | null>(null);

  // フォルダデータをリアルタイム監視
  const fetchFolders = useCallback((workspaceId: string) => {
    if (!workspaceId) {
      return;
    }

    if (!auth.currentUser) {
      return;
    }

    // 既存のリスナーを停止
    if (unsubscribe) {
      unsubscribe();
      setUnsubscribe(null);
    }

    // 同じワークスペースの場合は既に監視中なので何もしない
    if (currentWorkspaceId === workspaceId) {
      return;
    }

    setIsLoading(true);
    setError(null);
    setCurrentWorkspaceId(workspaceId);

    try {
      const foldersRef = collection(db, 'workspaces', workspaceId, 'folders');
      const q = query(foldersRef, orderBy('order', 'asc'));
      
      const unsubscribeFn = onSnapshot(q, (querySnapshot) => {
        const foldersData: FolderData[] = [];
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          foldersData.push({
            id: doc.id,
            name: data.name || '',
            isEditing: false,
            parentId: data.parentId || 'root',
            ancestorFolderIds: data.ancestorFolderIds || ['root'],
            order: data.order || 0
          });
        });

        setFolders(foldersData);
        setIsLoading(false);
      }, (error) => {
        console.error('❌ [FolderContext] リアルタイムリスナーエラー:', {
          error,
          errorCode: (error as any)?.code,
          errorMessage: (error as any)?.message,
          workspaceId,
          timestamp: new Date().toISOString()
        });
        setError(uiText("Failed to watch folder data"));
        setIsLoading(false);
      });

      unsubscribeRef.current = unsubscribeFn;
      setUnsubscribe(() => unsubscribeFn);
    } catch (error) {
      console.error('❌ [FolderContext] リスナー設定エラー:', {
        error,
        errorCode: (error as any)?.code,
        errorMessage: (error as any)?.message,
        workspaceId,
        timestamp: new Date().toISOString()
      });
      setError(uiText("Failed to set up folder data watching"));
      setIsLoading(false);
    }
  }, [currentWorkspaceId, unsubscribe]);

  // 認証が外れたら、前のワークスペースのフォルダ購読を残さない
  useEffect(() => {
    if (user) return;
    const stop = unsubscribeRef.current;
    if (!stop) return;
    stop();
    unsubscribeRef.current = null;
    setUnsubscribe(null);
    setCurrentWorkspaceId(null);
  }, [user, currentWorkspaceId]);

  // コンポーネントのクリーンアップ時にリスナーを停止
  useEffect(() => {
    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [unsubscribe]);

  // フォルダを作成
  const createFolder = async (workspaceId: string, folderData: Omit<FolderData, 'id' | 'isEditing'>) => {
    const folderId = `folder_${Date.now()}`;
    
    try {
      const folderRef = doc(db, 'workspaces', workspaceId, 'folders', folderId);

      // ancestorFolderIdsの計算：親フォルダーのancestorFolderIds + 親フォルダーのID
      let ancestorFolderIds: string[];
      if (folderData.parentId === 'root' || !folderData.parentId) {
        // ルートレベルの場合は['root']のみ
        ancestorFolderIds = ['root'];
      } else {
        // 親フォルダーを検索
        const parentFolder = folders.find(f => f.id === folderData.parentId);
        if (parentFolder) {
          // 親フォルダーのancestorFolderIds + 親フォルダーのID
          const parentAncestors = parentFolder.ancestorFolderIds || ['root'];
          ancestorFolderIds = [...parentAncestors, folderData.parentId];
        } else {
          // 親フォルダーが見つからない場合は親IDのみ
          ancestorFolderIds = [folderData.parentId];
        }
      }

      await setDoc(folderRef, {
        name: folderData.name,
        parentId: folderData.parentId || 'root',
        ancestorFolderIds: ancestorFolderIds,
        order: folderData.order || 0
      });

      // ローカル状態を更新
      setFolders(prev => [...prev, {
        id: folderId,
        ...folderData,
        ancestorFolderIds,
        isEditing: false
      }]);

      return folderId;
    } catch (error) {
      console.error('フォルダ作成エラー:', error);
      throw error;
    }
  };

  // フォルダを更新（Firestore更新のみ、ローカル状態はリアルタイムリスナーで自動更新）
  const updateFolder = async (workspaceId: string, folderId: string, updates: Partial<FolderData>) => {
    try {
      const folderRef = doc(db, 'workspaces', workspaceId, 'folders', folderId);
      const updateData: any = {};

      if (updates.name !== undefined) updateData.name = updates.name;
      if (updates.parentId !== undefined) updateData.parentId = updates.parentId;
      if (updates.ancestorFolderIds !== undefined) updateData.ancestorFolderIds = updates.ancestorFolderIds;
      if (updates.order !== undefined) updateData.order = updates.order;

      await updateDoc(folderRef, updateData);
    } catch (error) {
      console.error('❌ [FolderContext] フォルダ更新エラー:', error);
      throw error;
    }
  };

  // フォルダを削除（Cloud Functionsでボード移動処理を実行）
  const deleteFolder = async (workspaceId: string, folderId: string) => {
    try {
      // 子フォルダを再帰的に取得
      const getChildFolders = (parentId: string): string[] => {
        const children = folders.filter(folder => folder.parentId === parentId);
        let allChildren: string[] = [];
        
        children.forEach(child => {
          allChildren.push(child.id);
          // 孫フォルダも再帰的に取得
          allChildren = allChildren.concat(getChildFolders(child.id));
        });
        
        return allChildren;
      };

      // 削除対象のフォルダIDを取得（自分 + 全ての子フォルダ）
      const childFolders = getChildFolders(folderId);
      const foldersToDelete = [folderId, ...childFolders];

      // ⚠️ 重要: 親フォルダーのみを先に削除
      // 子フォルダーはまだ存在する状態でCloud Functionsトリガーを発火させる
      const parentFolderRef = doc(db, 'workspaces', workspaceId, 'folders', folderId);
      await deleteDoc(parentFolderRef);

      // Cloud Functionsでボード移動処理が完了するまで待機
      await new Promise(resolve => setTimeout(resolve, 3000));

      // 子フォルダーを削除（ボード移動処理完了後）
      if (childFolders.length > 0) {
        const batch = writeBatch(db);
        childFolders.forEach(childFolderId => {
          const childFolderRef = doc(db, 'workspaces', workspaceId, 'folders', childFolderId);
          batch.delete(childFolderRef);
        });

        await batch.commit();
      }

      // ローカル状態を更新（削除されたフォルダを全て除外）
      setFolders(prev => prev.filter(folder => !foldersToDelete.includes(folder.id)));

      // 削除されたフォルダが選択されていた場合は選択をクリア
      if (selectedFolderId && foldersToDelete.includes(selectedFolderId)) {
        setSelectedFolderId(null);
      }

      // 削除されたフォルダが開かれていた場合は開閉状態から除外
      setOpenFolderIds(prev => {
        const newOpenIds = new Set(prev);
        foldersToDelete.forEach(id => newOpenIds.delete(id));
        return newOpenIds;
      });
    } catch (error) {
      console.error('❌ [FolderContext] フォルダ削除エラー:', error);
      throw error;
    }
  };

  // フォルダデータをクリア
  const clearFolders = () => {
    setFolders([]);
    setSelectedFolderId(null);
    setOpenFolderMenuId(null);
    setOpenFolderIds(new Set());
    setError(null);
  };

  // フォルダの開閉状態を切り替え
  const toggleFolderOpen = useCallback((folderId: string) => {
    setOpenFolderIds(prev => {
      // 🔧 非破壊的なSet操作（Reactが変更を検知できるように）
      const wasOpen = prev.has(folderId);

      let newSet;
      if (wasOpen) {
        // 閉じる：新しいSetを作成して削除
        newSet = new Set(Array.from(prev).filter(id => id !== folderId));
      } else {
        // 開く：新しいSetを作成して追加
        newSet = new Set(Array.from(prev).concat(folderId));
      }

      return newSet;
    });
  }, [openFolderIds]);

  const value: FolderContextType = {
    folders,
    setFolders,
    selectedFolderId,
    setSelectedFolderId,
    openFolderMenuId,
    setOpenFolderMenuId,
    openFolderIds,
    setOpenFolderIds,
    isLoading,
    error,
    fetchFolders,
    createFolder,
    updateFolder,
    deleteFolder,
    clearFolders,
    toggleFolderOpen
  };

  return (
    <FolderContext.Provider value={value}>
      {children}
    </FolderContext.Provider>
  );
};
